import { apiClient, apiClientPaginated } from '@/lib/api-client';
import type {
  CancelSubscriptionPayload,
  CreatePlanPayload,
  DowngradeSubscriptionPayload,
  ExtendTrialPayload,
  InvoiceStatus,
  ReactivateSubscriptionPayload,
  RenewSubscriptionPayload,
  SubscriptionChange,
  SubscriptionDetails,
  SubscriptionInvoice,
  SubscriptionPlan,
  UpdatePlanPayload,
  UpgradeSubscriptionPayload,
} from '@/types/subscription';

function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    sp.set(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const subscriptionService = {
  // ─── Plan catalog ────────────────────────────────────────────────────────
  listPlans(includeInactive = false) {
    return apiClient<SubscriptionPlan[]>(
      `/subscription-plans${qs({ include_inactive: includeInactive || undefined })}`,
    );
  },

  getPlan(id: string) {
    return apiClient<SubscriptionPlan>(`/subscription-plans/${id}`);
  },

  createPlan(payload: CreatePlanPayload) {
    return apiClient<SubscriptionPlan>('/subscription-plans', { method: 'POST', body: payload });
  },

  updatePlan(id: string, payload: UpdatePlanPayload) {
    return apiClient<SubscriptionPlan>(`/subscription-plans/${id}`, {
      method: 'PATCH',
      body: payload,
    });
  },

  deactivatePlan(id: string) {
    return apiClient<SubscriptionPlan>(`/subscription-plans/${id}`, { method: 'DELETE' });
  },

  // ─── Tenant subscription ─────────────────────────────────────────────────
  getDetails(tenantId: string) {
    return apiClient<SubscriptionDetails>(`/tenants/${tenantId}/subscription`);
  },

  upgrade(tenantId: string, payload: UpgradeSubscriptionPayload) {
    return apiClient<Record<string, unknown>>(
      `/tenants/${tenantId}/subscription/upgrade`,
      { method: 'POST', body: payload },
    );
  },

  downgrade(tenantId: string, payload: DowngradeSubscriptionPayload) {
    return apiClient<Record<string, unknown>>(
      `/tenants/${tenantId}/subscription/downgrade`,
      { method: 'POST', body: payload },
    );
  },

  renew(tenantId: string, payload: RenewSubscriptionPayload) {
    return apiClient<Record<string, unknown>>(
      `/tenants/${tenantId}/subscription/renew`,
      { method: 'POST', body: payload },
    );
  },

  cancel(tenantId: string, payload: CancelSubscriptionPayload = {}) {
    return apiClient<Record<string, unknown>>(
      `/tenants/${tenantId}/subscription/cancel`,
      { method: 'POST', body: payload },
    );
  },

  extendTrial(tenantId: string, payload: ExtendTrialPayload) {
    return apiClient<Record<string, unknown>>(
      `/tenants/${tenantId}/subscription/extend-trial`,
      { method: 'POST', body: payload },
    );
  },

  reactivate(tenantId: string, payload: ReactivateSubscriptionPayload = {}) {
    return apiClient<Record<string, unknown>>(
      `/tenants/${tenantId}/subscription/reactivate`,
      { method: 'POST', body: payload },
    );
  },

  async downloadInvoice(tenantId: string, invoiceId: string, filenameHint?: string) {
    const url = `/tenants/${tenantId}/subscription/invoices/${invoiceId}/download`;
    const { apiBlob } = await import('@/lib/api-client');
    const { blob, filename } = await apiBlob(url);
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename ?? filenameHint ?? `orio360-invoice-${invoiceId.slice(0, 8)}.txt`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  },

  listInvoices(
    tenantId: string,
    opts: { status?: InvoiceStatus; page?: number; limit?: number } = {},
  ) {
    const path = `/tenants/${tenantId}/subscription/invoices${qs({
      status: opts.status,
      page: opts.page,
      limit: opts.limit,
    })}`;
    // The response envelope carries `data` and `meta` side by side.
    return apiClientPaginated<SubscriptionInvoice[]>(path);
  },

  getInvoice(tenantId: string, invoiceId: string) {
    return apiClient<SubscriptionInvoice>(
      `/tenants/${tenantId}/subscription/invoices/${invoiceId}`,
    );
  },

  getHistory(tenantId: string, opts: { page?: number; limit?: number } = {}) {
    const path = `/tenants/${tenantId}/subscription/history${qs({
      page: opts.page,
      limit: opts.limit,
    })}`;
    return apiClientPaginated<SubscriptionChange[]>(path);
  },
};
